import { Link } from 'react-router-dom';
import { CreditCard, ArrowLeft } from 'lucide-react';

const LegalLayout = ({ title, children }) => (
  <div className="min-h-screen bg-[#020617]">
    <header className="glass-heavy border-b border-white/5">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">MeTag</span>
          </Link>
          <Link to="/" className="text-slate-400 hover:text-white flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </header>

    <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl font-bold text-white mb-8">{title}</h1>
      <div className="prose prose-invert max-w-none">
        {children}
      </div>
    </main>

    <footer className="border-t border-slate-800 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-slate-500 text-sm">
        © {new Date().getFullYear()} MeTag.cc. All rights reserved.
      </div>
    </footer>
  </div>
);

export default function TermsConditions() {
  return (
    <LegalLayout title="Terms & Conditions">
      <div className="space-y-6 text-slate-300" data-testid="terms-conditions-page">
        <p className="text-slate-400">Last updated: December 2024</p>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">1. Acceptance of Terms</h2>
          <p>By accessing and using MeTag.cc, you accept and agree to be bound by the terms and provision of this agreement.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">2. Products and Services</h2>
          <p>MeTag provides NFC-enabled smart cards that allow users to share digital profiles. Features include:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>NFC tap functionality for compatible smartphones</li>
            <li>QR code fallback for universal access</li>
            <li>Dashboard for managing linked URLs</li>
            <li>Dynamic URL updates at any time</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">3. Ordering and Payment</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>All prices are listed in Indian Rupees (INR)</li>
            <li>Payment is processed securely through Razorpay</li>
            <li>Orders are confirmed upon successful payment</li>
            <li>We reserve the right to cancel orders in case of pricing errors</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">4. Shipping Policy</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Orders are processed within 1-3 business days</li>
            <li>Standard delivery takes 3-7 business days within India</li>
            <li>Shipping is free for all orders</li>
            <li>Tracking information will be provided via email</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">5. User Account</h2>
          <p>You are responsible for:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Maintaining the confidentiality of your account</li>
            <li>All activities that occur under your account</li>
            <li>Ensuring the URLs you link do not violate any laws</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">6. Prohibited Content</h2>
          <p>You may not link your MeTag card to URLs containing:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Illegal content</li>
            <li>Malware or phishing sites</li>
            <li>Content that infringes on intellectual property rights</li>
            <li>Hate speech or discriminatory content</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">7. Limitation of Liability</h2>
          <p>MeTag shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the service.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">8. Governing Law</h2>
          <p>These terms shall be governed by and construed in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Ludhiana, Punjab.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">9. Contact</h2>
          <p>For questions about these Terms, contact us at support@metag.com</p>
        </section>
      </div>
    </LegalLayout>
  );
}
